/**
 * linkedin-signin v1.3 (2026-09-30, cc_tasks row 310, T344: the destination is stashed and read
 *   through src/lib/post-auth-route.ts (one sanitizer). On return Auth.tsx sends the person there
 *   FIRST and offers onboarding afterwards; see decidePostAuthRoute.)
 * linkedin-signin v1.2 (2026-09-30, cc_tasks row 297, T332: redirectTo drops oauth_error and
 *   provider, the failed-return notice ./oauth-return-error.ts puts on /auth.)
 * linkedin-signin v1.1(2026-09-30, cc_tasks row 284, T319: the same path now serves Continue
 *   with Google, startOAuthSignIn("google"). Google used the Lovable OAuth broker at
 *   /~oauth/initiate, which does not exist on this host (404 page), so every Google press
 *   dead-ended. googleSignInAvailable() asks the auth server whether Google is switched on;
 *   the button shows only when it is. The press is the funnel's signup_start, see
 *   ./signup-funnel.ts.)
 * linkedin-signin v1.0 (2026-09-29, cc_tasks row 260, T299)
 *
 * "Continue with LinkedIn" for every sign-in and sign-up surface. One place, so
 * the Auth page and the shared-dossier gate behave identically.
 *
 * ── WHERE THE VISITOR WAS GOING SURVIVES THE ROUND TRIP ────────────────────
 *
 * LinkedIn sign-in leaves the site. Two things carry the destination back:
 *  1. redirectTo is the page the button was pressed on, query string included.
 *     The project's redirect allow-list covers mentionfox.com/** (checked
 *     2026-09-29 with an invalid-token probe: /auth?next=... came back intact,
 *     a foreign host fell back to the site root).
 *  2. The same localStorage stashes Auth.tsx already reads after any sign-in:
 *     mf_next (+ mf_next_at) for ?next= / ?return_to=, and mf_checkout_intent
 *     for ?plan= / ?pack=. A preview host that is not on the allow-list lands on
 *     the site root and still finds them.
 * After sign-in Auth.tsx handlePostAuth runs the usual order: claimed run,
 * reward target, checkout intent, then onboarding. Nothing new decides the order.
 *
 * ── SCOPES ─────────────────────────────────────────────────────────────────
 * The provider's default request is openid, profile, email, and nothing is
 * added here. The app never posts to LinkedIn from this sign-in.
 */

import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { rememberCheckoutIntent } from "@/lib/checkout-intent";
import { trackEvent } from "@/lib/analytics";
import { recordSignupStart } from "@/lib/signup-funnel";
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/lib/supabaseKeys";
import { sanitizeNext, stashNext } from "@/lib/post-auth-route";

export const LINKEDIN_TOOLTIP = "Sign in with your LinkedIn account; we never post without your approval.";
/** T345: tooltip on every Continue with Google button (src/components/auth/GoogleSignInButton.tsx). */
export const GOOGLE_TOOLTIP = "Sign in with your Google account; we only read your name and email address.";

const PENDING_KEY = "mf_oauth_pending";

function safeInternal(v: string | null): string | null {
  return sanitizeNext(v);
}

/**
 * Whether the auth server has Google sign-in switched on (public settings, no secret involved).
 * Cached for the page's life. False on any failure, so a dead button is never shown.
 */
let googleAvail: Promise<boolean> | null = null;
export function googleSignInAvailable(): Promise<boolean> {
  if (!googleAvail) {
    googleAvail = fetch(`${SUPABASE_URL}/auth/v1/settings`, { headers: { apikey: SUPABASE_PUBLISHABLE_KEY } })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => !!(j && j.external && j.external.google === true))
      .catch(() => false);
  }
  return googleAvail;
}

/** Store where the visitor is headed, then leave for LinkedIn. Returns an error message or null. */
export async function startLinkedInSignIn(opts: { persona?: string; returnTo?: string } = {}): Promise<string | null> {
  return startOAuthSignIn("linkedin_oidc", opts);
}

/** Store where the visitor is headed, then leave for the provider. Returns an error message or null. */
export async function startOAuthSignIn(provider: "linkedin_oidc" | "google", opts: { persona?: string; returnTo?: string } = {}): Promise<string | null> {
  const method = provider === "google" ? "google" : "linkedin";
  try {
    const params = new URLSearchParams(window.location.search);
    const next = safeInternal(opts.returnTo || null) || safeInternal(params.get("next") || params.get("return_to"));
    if (next) stashNext(next);
    // What they came to buy. /pricing writes this itself before sending anyone
    // here; an /auth?plan= link from anywhere else only has the URL, which the
    // round trip may not keep.
    if (!localStorage.getItem("mf_checkout_intent")) {
      const pack = params.get("pack");
      const plan = params.get("plan");
      if (pack) rememberCheckoutIntent({ kind: "pack", sku: pack.slice(0, 64) });
      else if (plan && plan !== "free") rememberCheckoutIntent({ kind: "plan", plan: plan.slice(0, 64) });
    }
    localStorage.setItem(PENDING_KEY, JSON.stringify({ method, at: Date.now() }));
  } catch { /* storage blocked: redirectTo still carries the query string */ }

  trackEvent("signup_started", { method, persona: opts.persona || "" });
  // The funnel's signup_start: a press, never an arrival (T319).
  recordSignupStart(method, opts.returnTo || null);

  // A surface that is not the Auth page but wants the Auth page's post-sign-in
  // order (reward first, onboarding after) passes returnTo and comes back via
  // /auth?next=. Otherwise the visitor returns to the page they pressed it on.
  const back = safeInternal(opts.returnTo || null);
  // T332: a retry after a failed return starts from /auth?...&oauth_error=...&provider=...;
  // those two are dropped so a successful second try does not come back showing the old error.
  const here = (() => {
    const u = new URL(window.location.href.split("#")[0]);
    u.searchParams.delete("oauth_error");
    u.searchParams.delete("provider");
    return u.toString();
  })();
  const redirectTo = back
    ? `${window.location.origin}/auth?next=${encodeURIComponent(back)}`
    : here;

  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo },
  });
  return error ? error.message : null;
}

/**
 * The funnel's signup_completed step, method=linkedin, recorded once for an
 * account LinkedIn just created. The server-side funnel (funnel_daily) already
 * counts every new profile; this is the client event that names the method,
 * matching the one email signup sends.
 */
export function recordOAuthSignupCompleted(user: User | null | undefined, persona?: string): void {
  try {
    if (!user) return;
    const provider = String(user.app_metadata?.provider || "");
    if (provider !== "linkedin_oidc" && provider !== "google") return;
    try { localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
    const created = Date.parse(user.created_at || "");
    if (!Number.isFinite(created) || Date.now() - created > 30 * 60 * 1000) return; // an existing account signing in
    const key = `mf_signup_completed:${user.id}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    trackEvent("signup_completed", { method: provider === "google" ? "google" : "linkedin", persona: persona || "" });
  } catch { /* analytics is never worth a failed sign-in */ }
}
